import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import useRouting from "../hooks/useRouting";
import "./Header.css";
import ayuashPayLogo from "../assets/images/ayuashPayLogo.svg";
import arrow_back from "../assets/images/arrow_back.svg";
import environment from "../environment";

const Header = ({ showBackButton = false, onBackClick = null, disableScrollHide = false }) => {
  const [logoUrl, setLogoUrl] = useState("");
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const { anchor } = useRouting();

  const hasFetchedLogoRef = useRef(false);
  
  // Fetch logo when component mounts
  useEffect(() => {
    // Prevent duplicate API calls in StrictMode
    if (hasFetchedLogoRef.current || !anchor) {
      return;
    }
    hasFetchedLogoRef.current = true;
    
    const fetchLogo = async () => {
      try {
        const anchorCode = anchor.toUpperCase();
        const response = await axios.get(
          `${environment.apiurl_agentCode}anchors/${anchorCode}/get_logo/`
        );

        if (response?.data?.msg?.anchor?.logo_url) {
          setLogoUrl(response.data.msg.anchor.logo_url);
          // Store logo URL in localStorage for use in other components
          localStorage.setItem("logoUrl", response.data.msg.anchor.logo_url);
        }
      } catch (error) {
        console.error("Error fetching logo:", error);
        // Don't show error to user for logo fetch failures
        hasFetchedLogoRef.current = false; // Reset on error to allow retry
      }
    };

    fetchLogo();
  }, [anchor]);

  // Handle scroll behavior - skip if scroll hide is disabled
  useEffect(() => {
    // If scroll hide is disabled, always show header
    if (disableScrollHide) {
      // Initialize visibility state based on prop - this is intentional initialization
      setIsVisible(true);
      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY || window.pageYOffset;
      const scrollThreshold = 50; // Threshold to determine if at top (when first component is visible)
      const isScrollingDown = currentScrollY > lastScrollY.current;
      const isScrollingUp = currentScrollY < lastScrollY.current;
      const isAtTop = currentScrollY <= scrollThreshold;

      // Always show header when at or near the top (first component visible)
      if (isAtTop) {
        setIsVisible(true);
      } 
      // Hide header when scrolling down
      else if (isScrollingDown) {
        setIsVisible(false);
      }
      // When scrolling up but not at top yet, keep header hidden
      // Don't show immediately on upward scroll - only when reaching the top
      else if (isScrollingUp) {
        setIsVisible(false);
      }

      lastScrollY.current = currentScrollY;
    };

    // Set initial scroll position
    lastScrollY.current = window.scrollY || window.pageYOffset;
    
    // Check initial position - show header if at top
    if (lastScrollY.current <= 50) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [disableScrollHide]);

  return (
    <div className={`header sm:px-4 md:px-6 lg:px-8 ${isVisible ? 'header-visible' : 'header-hidden'}`}>
      {showBackButton && (
        <button className="back-button" onClick={onBackClick}>
          <img src={arrow_back} alt="arrow_back" className="h-[20px] sm:h-[22px] md:h-[25px] w-[20px] sm:w-[22px] md:w-[25px]"/>
        </button>
      )}
      <div className="logo">
        <img
          src={ayuashPayLogo}
          alt="AyushPay Logo"
          className="ayuashPayLogo h-[18px] sm:h-[20px] md:h-[23px]"
        />

        {logoUrl && (
          <>
            <span className="logo-separator">|</span>
            <img src={logoUrl} alt="Partner Logo" className="partner-logo h-[18px] sm:h-[20px] md:h-[23px]" />
          </>
        )}
      </div>
    </div>
  );
};

export default Header;
